import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Alert,
  MenuItem,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import dayjs from 'dayjs';
import { useRecordRefundMutation } from '../../Service/extendedInvoicesApi';
import type { RecordRefundRequest } from '../../Types/paymentTypes';
import { invoiceLogger } from '../../utils/invoiceLogger';
import { extractApiErrorMessage } from '../../../../Utils/apiErrorUtils';
import { applyApiValidationErrorsToForm } from '../../utils/invoiceApiErrorHandling';

interface RecordRefundDialogProps {
  open: boolean;
  onClose: () => void;
  invoiceId: number;
  paidAmount: number;
  maxRefundAmount: number;
}

interface RefundFormData {
  amount: number;
  refundDate: string;
  refundMethod: string;
  note?: string; // Optional field
}

const REFUND_METHODS = [
  'Gotówka',
  'Karta płatnicza',
  'PayPal',
  'Stripe',
  'Inne',
];

// Validation schema
const createValidationSchema = (maxAmount: number): yup.ObjectSchema<RefundFormData> =>
  yup.object({
    amount: yup
      .number()
      .required('Kwota jest wymagana')
      .positive('Kwota musi być dodatnia')
      .max(maxAmount, `Kwota zwrotu nie może przekroczyć ${maxAmount.toFixed(2)} PLN`)
      .test('max-decimals', 'Maksymalnie 2 miejsca po przecinku', (value) => {
        if (!value) return true;
        return /^\d+(\.\d{1,2})?$/.test(value.toString());
      }),
    refundDate: yup
      .string()
      .required('Data zwrotu jest wymagana')
      .test('not-future', 'Data zwrotu nie może być w przyszłości', (value) => {
        if (!value) return true;
        const selectedDate = dayjs(value);
        const tomorrow = dayjs().add(1, 'day').startOf('day');
        return selectedDate.isBefore(tomorrow);
      }),
    refundMethod: yup.string().required('Metoda zwrotu jest wymagana').max(50),
    note: yup.string().max(500, 'Notatka nie może przekraczać 500 znaków').optional(),
  });

/**
 * Dialog for recording a refund for an invoice.
 * Validates that refund amount doesn't exceed paid amount.
 */
const RecordRefundDialog: React.FC<RecordRefundDialogProps> = ({
  open,
  onClose,
  invoiceId,
  paidAmount,
  maxRefundAmount,
}) => {
  const [recordRefund, { isLoading, error }] = useRecordRefundMutation();
  const [submitErrorMessage, setSubmitErrorMessage] = React.useState<string | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<RefundFormData>({
    resolver: yupResolver(createValidationSchema(maxRefundAmount)),
    defaultValues: {
      amount: 0,
      refundDate: dayjs().format('YYYY-MM-DD'),
      refundMethod: 'Karta płatnicza',
      note: '',
    },
  });

  const onSubmit = async (data: RefundFormData) => {
    clearErrors();
    setSubmitErrorMessage(null);

    try {
      const request: RecordRefundRequest = {
        amount: data.amount, // Backend converts to negative
        refundDate: data.refundDate,
        refundMethod: data.refundMethod,
        note: data.note || undefined,
      };

      await recordRefund({ invoiceId, data: request }).unwrap();

      // Success - close dialog and reset form
      reset();
      onClose();
    } catch (err) {
      applyApiValidationErrorsToForm(err, setError);
      setSubmitErrorMessage(
        extractApiErrorMessage(err, 'Błąd podczas rejestracji zwrotu. Sprawdź dane i spróbuj ponownie.')
      );
      invoiceLogger.error('Failed to record refund:', err);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      clearErrors();
      setSubmitErrorMessage(null);
      reset();
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Rejestracja zwrotu</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          {/* API Error */}
          {(submitErrorMessage || error) && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitErrorMessage ||
                extractApiErrorMessage(
                  error,
                  'Błąd podczas rejestracji zwrotu. Sprawdź dane i spróbuj ponownie.'
                )}
            </Alert>
          )}

          {/* Warning */}
          <Alert severity="warning" sx={{ mb: 2 }}>
            Zwrot spowoduje zmniejszenie wpłaconej kwoty. Maksymalna kwota zwrotu:{' '}
            <strong>{maxRefundAmount.toFixed(2)} PLN</strong>
          </Alert>

          <Grid container spacing={2}>
            {/* Amount */}
            <Grid size={{ xs: 12 }} >
              <Controller
                name="amount"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Kwota zwrotu *"
                    type="number"
                    fullWidth
                    error={!!errors.amount}
                    helperText={
                      errors.amount?.message ||
                      `Maksymalna kwota: ${maxRefundAmount.toFixed(2)} PLN`
                    }
                    inputProps={{
                      step: '0.01',
                      min: '0.01',
                      max: maxRefundAmount,
                    }}
                  />
                )}
              />
            </Grid>

            {/* Refund Date */}
            <Grid size={{ xs: 12 }} >
              <Controller
                name="refundDate"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Data zwrotu *"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.refundDate}
                    helperText={errors.refundDate?.message}
                  />
                )}
              />
            </Grid>

            {/* Refund Method */}
            <Grid size={{ xs: 12 }} >
              <Controller
                name="refundMethod"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    label="Metoda zwrotu *"
                    fullWidth
                    error={!!errors.refundMethod}
                    helperText={errors.refundMethod?.message}
                  >
                    {REFUND_METHODS.map((method) => (
                      <MenuItem key={method} value={method}>
                        {method}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>

            {/* Note */}
            <Grid size={{ xs: 12 }} >
              <Controller
                name="note"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Notatka (opcjonalnie)"
                    fullWidth
                    multiline
                    rows={3}
                    error={!!errors.note}
                    helperText={errors.note?.message || `${(field.value || '').length}/500 znaków`}
                    placeholder="Powód zwrotu, np. nadpłata, anulowanie rezerwacji..."
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={isLoading}>
            Anuluj
          </Button>
          <Button type="submit" variant="contained" color="warning" disabled={isLoading}>
            {isLoading ? 'Zapisywanie...' : 'Zapisz zwrot'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default RecordRefundDialog;
